/**
 * AN ACCOUNT'S NAME ON THE MODELLING SCREENS, TOLD APART FROM ITS TWIN (owner, 22 Oct 2026: "they might each
 * have an ABP with the same institution … I might just type in MLC for both").
 *
 * Where a couple's file has two accounts with the same description, each one owned by one of them gains its
 * owner's first name: "MLC ABP – Matthew", "MLC ABP – Sally". Everything else keeps the description as typed:
 * a unique description, a single client, a joint account, or a description that already names its owner.
 * Display only: the description on the file and every funding key are untouched.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';

type Named = { description?: unknown; ownership?: unknown } | null | undefined;

const norm = (s: unknown) => String(s ?? '').trim().toLowerCase();

/** How many accounts on the file carry this description (all kinds of asset). */
function descriptionCount(factFind: DraftFactFind | null | undefined, desc: string): number {
    const assets = (factFind?.assets || {}) as Record<string, unknown>;
    let n = 0;
    for (const list of Object.values(assets)) {
        if (!Array.isArray(list)) continue;
        for (const a of list) if (norm((a as Named)?.description) === desc) n += 1;
    }
    return n;
}

export function assetDisplayName(asset: Named, factFind: DraftFactFind | null | undefined, fallback = 'account'): string {
    const raw = String(asset?.description ?? '').trim();
    if (!raw) return fallback;
    if (factFind?.adviceFor !== 'couple') return raw;
    const ownership = String(asset?.ownership ?? '');
    const owner = ownership === 'client' ? factFind.client?.firstName : ownership === 'partner' ? factFind.partner?.firstName : null;
    const name = String(owner ?? '').trim();
    if (!name || raw.toLowerCase().includes(name.toLowerCase())) return raw;
    return descriptionCount(factFind, norm(raw)) > 1 ? `${raw} – ${name}` : raw;
}
